import Loader from "./components/base/Loader";
import { usePermissionsGate } from "./components/layout/PermissionsGate";
import TechnicalError from "./components/layout/TechnicalError";
import { withCachePersistorProvider } from "./context/cache-persistor-context";
import { withNotificationsProvider } from "./context/notifications-context";
import { PreservedQueryParamsProvider } from "./context/preserved-query-params-context";
import {
  MethodType,
  OnboardStatus,
  OrganizationStatus,
  useGetOrganizationsQuery,
  useGetRolesQuery,
  useGetTrialFinishDateQuery,
  useMakeOrganizationMutation,
} from "./graphql/codegen/generated";
import { withApolloClient } from "./hoc/withApolloClient";
import withMaintenance from "./hoc/withMaintenance";
import useInternalUser from "./hooks/useInternalUser";
import Inactive from "./pages/inactive";
import { SCOPES } from "./permission-maps";
import { systemZone } from "./readonly-variables";
import { useWatchUsers } from "./subscriptions";
import useWatchOnboardStatus from "./subscriptions/useWatchOnboardStatus";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import classNames from "classnames";
import { DateTime, Settings } from "luxon";
import { FC, Suspense, lazy, useCallback, useEffect, useMemo } from "react";
import { isMobile } from "react-device-detect";
import { withErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  Route,
  Routes,
  useMatch,
  useNavigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import Header from "~@/components/layout/Header";
import WarningBanner from "~@/components/layout/WarningBanner";
import ServerError from "~@/pages/500";
import useWatchNotifications from "~@/subscriptions/useWatchNotifications";
import useWatchOrganizationStatus from "~@/subscriptions/useWatchOrganizationStatus";
import useWatchPushNotifications from "~@/subscriptions/useWatchPushNotifications";

const Home = lazy(() => import("./pages/home"));
const Onboarding = lazy(() => import("./pages/onboarding"));
const MultiAccount = lazy(() => import("./pages/multi-account"));
const MobilePlaceholder = lazy(() => import("./pages/mobile-placeholder"));

const App: FC = () => {
  const match = useMatch("/bot-builder/*");
  const { i18n } = useTranslation();

  const { t } = useTranslation();

  const navigate = useNavigate();

  const { loading: areRolesLoading, error: rolesError } = useGetRolesQuery();

  const { internalUser, isInternalUserLoading, internalUserError, isRoot } =
    useInternalUser();

  const [makeOrganization] = useMakeOrganizationMutation();

  const {
    data: organizationsData,
    loading: areOrganizationsLoading,
    error: organizationsError,
  } = useGetOrganizationsQuery();

  const allowedToViewOnboarding = usePermissionsGate([
    SCOPES.AllowedToViewOnboarding,
  ]);

  const currentOrganization = useMemo(() => {
    return organizationsData?.getOrganizations?.current;
  }, [organizationsData]);

  const resetOrganization = useCallback(() => {
    if (!currentOrganization) return;

    return makeOrganization({
      variables: {
        input: {
          id: currentOrganization.id,
          method: MethodType.Update,
          onboardStatus: OnboardStatus.ChooseTariff,
        },
      },
      optimisticResponse: {
        makeOrganization: {
          ...currentOrganization,
          onboardStatus: OnboardStatus.ChooseTariff,
        },
      },
    });
  }, [currentOrganization, makeOrganization]);

  const { data: trialFinishDateData } = useGetTrialFinishDateQuery({
    skip: !currentOrganization?.isTrial,
  });

  const trialFinishDay = useMemo(() => {
    if (!trialFinishDateData?.getTrialFinishDate) return null;

    return DateTime.fromISO(trialFinishDateData?.getTrialFinishDate).toFormat(
      "MMMM dd"
    );
  }, [trialFinishDateData]);

  const currentOrganizationTimeZone = useMemo(() => {
    return currentOrganization?.timezone ?? systemZone.name;
  }, [currentOrganization]);

  const showOnboarding = useMemo(() => {
    return (
      allowedToViewOnboarding &&
      currentOrganization?.onboardStatus !== OnboardStatus.Done
    );
  }, [allowedToViewOnboarding, currentOrganization]);

  const isInactive = useMemo(() => {
    return (
      (currentOrganization?.status === OrganizationStatus.Inactive &&
        (currentOrganization.isCanceled ||
          currentOrganization.isPaymentFailed)) ??
      false
    );
  }, [currentOrganization]);

  const parentOID = useMemo(() => {
    return currentOrganization?.parentOID;
  }, [currentOrganization]);

  const renderRoutes = useCallback(() => {
    if (isMobile && currentOrganization?.onboardStatus !== OnboardStatus.Done) {
      return (
        <Routes>
          <Route path="*" element={<MobilePlaceholder />} />
        </Routes>
      );
    }

    if (!isRoot) {
      return (
        <Routes>
          {isInactive ? (
            <Route path="*" element={<Inactive />} />
          ) : (
            <>
              <Route
                path="*"
                element={
                  showOnboarding ? (
                    <Navigate replace to={"/welcome"} />
                  ) : (
                    <Home />
                  )
                }
              />
              <Route
                path="welcome/*"
                element={
                  showOnboarding ? (
                    <Onboarding />
                  ) : (
                    <Navigate replace to={"/"} />
                  )
                }
              />
            </>
          )}
        </Routes>
      );
    }

    if (typeof parentOID === "string") {
      return (
        <Routes>
          <Route path="*" element={<Home />} />
          <Route path="multi-account/*" element={<MultiAccount />} />
        </Routes>
      );
    }

    return (
      <Routes>
        <Route path="*" element={<MultiAccount />} />
      </Routes>
    );
  }, [parentOID, showOnboarding, isRoot, currentOrganization, isInactive]);

  useWatchUsers();

  useWatchNotifications(internalUser?.id ?? "");

  useWatchPushNotifications(internalUser?.id ?? "");

  useWatchOrganizationStatus(currentOrganization?.id ?? "");

  useWatchOnboardStatus();

  useEffect(() => {
    Settings.defaultZone = currentOrganizationTimeZone;
  }, [currentOrganizationTimeZone]);

  useEffect(() => {
    Settings.defaultLocale = "en-US";
  }, [i18n.language]);

  useEffect(() => {
    if (
      window.Notification &&
      window.Notification.permission !== "granted" &&
      window.Notification.permission !== "denied"
    )
      window.Notification.requestPermission();
  }, []);

  if (areRolesLoading || isInternalUserLoading || areOrganizationsLoading)
    return <Loader isFullHeight />;
  else if (rolesError || internalUserError || organizationsError)
    return (
      <ServerError
        error={
          rolesError
            ? "Failed to retrieve roles"
            : internalUserError
            ? "Failed to fetch the user data"
            : "Unable to load organizations"
        }
      />
    );
  else
    return (
      <PreservedQueryParamsProvider>
        {isMobile ||
        (isInactive && !isRoot) ||
        showOnboarding ? null : !currentOrganization?.isCanceled &&
          (currentOrganization?.isPaymentFailed ||
            currentOrganization?.status === OrganizationStatus.Inactive) ? (
          <WarningBanner
            leading={
              <>
                <span data-bold>
                  {t(
                    `info.${
                      currentOrganization.status === OrganizationStatus.Inactive
                        ? "inactive-error"
                        : "payment-error"
                    }.title`
                  )}
                </span>
                <span>
                  {t(
                    `info.${
                      currentOrganization.status === OrganizationStatus.Inactive
                        ? "inactive-error"
                        : "payment-error"
                    }.description`
                  )}
                </span>
              </>
            }
            action={
              <button
                className="button button--h-36 button--fill-black-0"
                type="button"
                onClick={() => navigate("/settings/payment-method")}
              >
                <span>{t("info.update-method")}</span>
              </button>
            }
          />
        ) : currentOrganization?.isCanceled ? (
          <WarningBanner
            leading={
              <>
                <span data-bold>{t(`info.account-canceled.title`)}</span>
                <span>{t(`info.account-canceled.description`)}</span>
              </>
            }
            action={
              <button
                className="button button--h-36 button--fill-black-0"
                type="button"
                onClick={() => navigate("/settings/billings/packages")}
              >
                <span>{t("action.activate-subscription")}</span>
              </button>
            }
          />
        ) : currentOrganization?.isTrial &&
          typeof trialFinishDay === "string" ? (
          <WarningBanner
            leading={
              <>
                <span>{t(`info.trial.title`)}</span>
                <span data-bold>{trialFinishDay}.</span>
              </>
            }
            action={
              <button
                className="button button--h-36 button--fill-black-0"
                type="button"
                onClick={resetOrganization}
              >
                <span>{t("action.see-more")}</span>
              </button>
            }
          />
        ) : null}

        {isRoot && !(showOnboarding && isMobile) ? <Header /> : null}

        <Suspense fallback={<Loader isFullHeight />}>{renderRoutes()}</Suspense>
        <ToastContainer
          className={classNames({
            "Toastify__toast-container--has-navbar-top":
              match instanceof Object,
          })}
          theme="dark"
          role="alert"
          limit={1}
          position="top-center"
          closeButton={false}
          hideProgressBar
          autoClose={2000}
        />
      </PreservedQueryParamsProvider>
    );
};

export default withAuthenticationRequired(
  withErrorBoundary(
    withCachePersistorProvider(
      withApolloClient(withNotificationsProvider(withMaintenance(App)))
    ),
    {
      FallbackComponent: () => <TechnicalError isFullheight />,
    }
  ),
  {
    onRedirecting() {
      return <Loader isFullHeight />;
    },
  }
);
